import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Spinner from '../../../components/Spinner';
import TenantUserService from '../../../services/tenant-user.service';
import { Modules, NonAuthRoutes } from '../../../enums';

type ValidationState = 'checking' | 'valid' | 'invalid';

const ActivateTenantUser: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();

  const [validation, setValidation] = useState<ValidationState>('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [tenantName, setTenantName] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidation('invalid');
      setErrorMessage(t('tenantUsers.activate.pageTexts.invalidToken'));
      return;
    }
    TenantUserService.validateToken(token)
      .then((response: any) => {
        const data = response?.data ?? {};
        setUserName(data.userName ?? '');
        setTenantName(data.tenantName ?? '');
        setValidation('valid');
      })
      .catch((err: any) => {
        setValidation('invalid');
        setErrorMessage(err?.response?.data?.message ?? t('tenantUsers.activate.pageTexts.invalidToken'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateForm = useCallback(() => {
    const nextErrors: Record<string, string> = {};
    if (password.length < 8) nextErrors.password = t('tenantUsers.activate.pageTexts.passwordTooShort');
    if (password !== confirmPassword) nextErrors.confirmPassword = t('tenantUsers.activate.pageTexts.passwordMismatch');
    return nextErrors;
  }, [password, confirmPassword, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!token) return;
      const nextErrors = validateForm();
      setFormErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setSubmitting(true);
      try {
        await TenantUserService.activate(token, password);
        setActivated(true);
        setTimeout(() => {
          navigate(`${Modules.auth}${NonAuthRoutes.login}`, { replace: true });
        }, 3000);
      } catch (err: any) {
        setValidation('invalid');
        setErrorMessage(err?.response?.data?.message ?? t('tenantUsers.activate.pageTexts.invalidToken'));
      } finally {
        setSubmitting(false);
      }
    },
    [token, password, validateForm, navigate, t]
  );

  if (validation === 'checking') {
    return (
      <div className="w-px-400 mx-auto pt-5 pt-lg-0 text-center">
        <h5 className="mb-4">{t('tenantUsers.activate.pageTexts.title')}</h5>
        <Spinner />
        <p className="text-muted small mt-3">{t('tenantUsers.activate.pageTexts.activating')}</p>
      </div>
    );
  }

  if (validation === 'invalid') {
    return (
      <div className="w-px-400 mx-auto pt-5 pt-lg-0 text-center">
        <h5 className="mb-3">{t('tenantUsers.activate.pageTexts.title')}</h5>
        <p className="text-danger">{errorMessage}</p>
        <p className="text-muted small">{t('tenantUsers.activate.pageTexts.contactAdmin')}</p>
      </div>
    );
  }

  if (activated) {
    return (
      <div className="w-px-400 mx-auto pt-5 pt-lg-0 text-center">
        <h5 className="mb-3">{t('tenantUsers.activate.pageTexts.title')}</h5>
        <p className="text-success">{t('tenantUsers.activate.pageTexts.success')}</p>
      </div>
    );
  }

  return (
    <div className="w-px-400 mx-auto pt-5 pt-lg-0">
      <h5 className="mb-1 text-center">{t('tenantUsers.activate.pageTexts.setPasswordTitle')}</h5>
      <p className="text-muted small text-center mb-4">
        {userName} — {tenantName}
      </p>
      <form onSubmit={handleSubmit} className="row g-4">
        <div className="col-12">
          <Input
            id="password"
            name="password"
            type="password"
            label={t('tenantUsers.activate.pageTexts.passwordLabel')}
            required
            value={password}
            error={!!formErrors.password}
            errorMessage={formErrors.password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="col-12">
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label={t('tenantUsers.activate.pageTexts.confirmPasswordLabel')}
            required
            value={confirmPassword}
            error={!!formErrors.confirmPassword}
            errorMessage={formErrors.confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>
        <div className="col-12">
          <Button type="submit" className="btn btn-primary w-100" text={t('tenantUsers.activate.pageTexts.submit')} loading={submitting} />
        </div>
      </form>
    </div>
  );
};

export default ActivateTenantUser;
